// Inbox triage (Session AMR, docs/specs/ELE-V2-SESSION-AMR-PROMPT.md). Replaces the external
// Task-Scheduler script's Label_142 management: the platform's own sweep now decides, per Gmail
// thread, whether ELE is simply awaiting a reply, the reply has gone quiet long enough to chase, or
// the customer's last message appears to need a response from ELE.
//
// A sweep that surfaces candidates for the office to act on, never one that silently auto-acts
// (the same shape LolerGmailSweepModal already uses) -- but unlike that modal's one-shot review,
// state here persists across sweeps, so this is a standing page, not a dismissible dialog.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDateTime } from "../../lib/format";
import type {
  InboxTriageDismissedThread,
  InboxTriageExcluded,
  InboxTriageStandingCounts,
  InboxTriageSweepOutcome,
  InboxTriageThread,
} from "../../lib/api";

type LoadState = "loading" | "ready" | "error";

function ageLabel(days: number): string {
  if (days <= 0) return "today";
  return `${days} working day${days === 1 ? "" : "s"} ago`;
}

// Session AMV: Gmail's own web permalink for a thread, so a flagged row can be opened and actually
// replied to (today the only row action is Dismiss). No existing gmail.com link exists anywhere
// else in this app to reuse -- checked, not assumed. #all/ rather than #inbox/, because the sweep
// covers the whole mailbox (in:anywhere), so a flagged thread may be archived rather than literally
// in the Inbox label; #inbox/ would not find it there. u/0 is Gmail's own default account slot: the
// platform has no way to know which slot the office's own browser session signs the connected
// mailbox into, so this is the same default every app linking to Gmail without that knowledge uses.
function gmailThreadHref(threadId: string): string {
  return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(threadId)}`;
}

// Session AMV: .ele-list-table is table-layout: fixed with no widths declared here, so the browser
// was sharing all 5 columns equally (~20% each) -- starving the subject/sender column, which is
// where the office actually needs the room, of the space its own whitespace-normal (now cell-wrap)
// styling needed to wrap into. Same shape used by all three ThreadRow tables below.
function ThreadColgroup() {
  return (
    <colgroup>
      <col style={{ width: "48%" }} />
      <col style={{ width: "16%" }} />
      <col style={{ width: "14%" }} />
      <col style={{ width: "12%" }} />
      <col style={{ width: "10%" }} />
    </colgroup>
  );
}

// Why a thread was suppressed, in the office's words rather than the stored key.
const EXCLUSION_LABEL: Record<string, string> = {
  self_correspondence: "ELE's own address",
  no_reply_sender: "A no-reply sender",
  automated_sender: "A known automated sender",
};

function ThreadRow({ t, onDismiss, dismissing }: { t: InboxTriageThread; onDismiss: (id: string) => void; dismissing: boolean }) {
  return (
    <tr>
      <td className="cell-wrap">
        <a
          href={gmailThreadHref(t.gmailThreadId)}
          target="_blank"
          rel="noopener noreferrer"
          title="Open this thread in Gmail"
          className="font-semibold text-ele-strong hover:text-ele-green hover:underline"
        >
          {t.subject?.trim() || "(no subject)"}
        </a>
        <div className="text-xs text-ele-body">{t.counterpartyName ? `${t.counterpartyName} <${t.counterpartyEmail ?? "?"}>` : (t.counterpartyEmail ?? "Unknown sender")}</div>
        {t.lastMessageSnippet && <div className="mt-1 text-xs text-ele-body/80">{t.lastMessageSnippet}</div>}
        {t.state === "action_owed" && t.contentReason && (
          <div className="mt-1 text-xs italic text-ele-body/70">
            {t.contentHighConfidence ? "High confidence: " : ""}
            {t.contentReason}
          </div>
        )}
      </td>
      <td className="whitespace-nowrap text-sm text-ele-body">{formatDateTime(t.lastMessageAt)}</td>
      <td className="whitespace-nowrap text-sm text-ele-body">{t.state === "action_owed" ? "" : ageLabel(t.ageWorkingDays)}</td>
      <td className="whitespace-nowrap">
        <StatusPill domain="triageState" status={t.state} />
      </td>
      <td className="whitespace-nowrap text-right">
        {t.state !== "awaiting_reply" && (
          <Button variant="secondary" size="sm" onClick={() => onDismiss(t.id)} disabled={dismissing}>
            {dismissing ? "Dismissing..." : "Dismiss"}
          </Button>
        )}
      </td>
    </tr>
  );
}

// Session AUB: also the "Chases due" tab of the Mail workspace (embedded: a section heading under the workspace title, not a
// second page title). Chasing itself is Phase 7; this is the existing sweep view, unchanged.
export default function InboxTriagePage({ embedded = false }: { embedded?: boolean } = {}) {
  const [state, setState] = useState<LoadState>("loading");
  const [threads, setThreads] = useState<InboxTriageThread[]>([]);
  const [dismissed, setDismissed] = useState<InboxTriageDismissedThread[]>([]);
  const [chaseThresholdDays, setChaseThresholdDays] = useState(5);
  const [agedThresholdDays, setAgedThresholdDays] = useState(30);
  const [excluded, setExcluded] = useState<InboxTriageExcluded | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [showAwaiting, setShowAwaiting] = useState(false);
  const [showAged, setShowAged] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [sweeping, setSweeping] = useState(false);
  const [sweepResult, setSweepResult] = useState<InboxTriageSweepOutcome[] | null>(null);
  const [sweepStanding, setSweepStanding] = useState<InboxTriageStandingCounts | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.inboxTriage.threads();
      setThreads(res.threads);
      setDismissed(res.recentlyDismissed);
      setChaseThresholdDays(res.chaseThresholdDays);
      setAgedThresholdDays(res.agedThresholdDays);
      setExcluded(res.excluded);
      setState("ready");
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function sweepNow() {
    setSweeping(true);
    setError(null);
    setSweepResult(null);
    setSweepStanding(null);
    try {
      const res = await api.inboxTriage.sweepNow();
      setSweepResult(res.outcomes);
      setSweepStanding(res.standing ?? null);
      await load();
    } catch {
      setError("Google is not connected, so the inbox cannot be swept. Connect it in Settings.");
    } finally {
      setSweeping(false);
    }
  }

  async function dismiss(id: string) {
    setBusyId(id);
    try {
      await api.inboxTriage.dismiss(id);
      await load();
    } catch {
      setError("Could not dismiss that thread. Please try again in a moment.");
    } finally {
      setBusyId(null);
    }
  }

  async function restore(id: string) {
    setBusyId(id);
    try {
      await api.inboxTriage.restore(id);
      await load();
    } catch {
      setError("Could not restore that thread. Please try again in a moment.");
    } finally {
      setBusyId(null);
    }
  }

  // `aged` is ORTHOGONAL to state, so the two working sections take the not-aged rows of each state
  // and the aged rows of BOTH states collect in one section below. An aged thread is still owed and
  // is still dismissible from there -- it has simply stopped being today's work.
  const actionOwed = threads.filter((t) => t.state === "action_owed" && !t.aged);
  const needsChase = threads.filter((t) => t.state === "needs_chase" && !t.aged);
  const aged = threads.filter((t) => t.state !== "awaiting_reply" && t.aged);
  const awaiting = threads.filter((t) => t.state === "awaiting_reply");

  const intro = (
    <>
      Which emails need a reply from ELE, and which have been waiting on a reply from someone else for more than {chaseThresholdDays}{" "}
      working day{chaseThresholdDays === 1 ? "" : "s"}. Set in Settings.
    </>
  );
  const sweepButton = (
    <Button size="sm" onClick={sweepNow} disabled={sweeping}>
      {sweeping ? "Sweeping..." : "Sweep now"}
    </Button>
  );

  return (
    <div className={embedded ? "" : "p-6"}>
      {embedded ? (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl text-ele-strong">Chases due</h2>
            <p className="mt-1 max-w-2xl text-sm text-ele-body">{intro}</p>
          </div>
          {sweepButton}
        </div>
      ) : (
        <PageHeader className="mb-4" title="Inbox triage" subtitle={intro} actions={sweepButton} />
      )}

      {error && <p role="alert" className="mb-4 text-sm text-ele-danger">{error}</p>}

      {sweepResult && (
        <div className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-panel p-3 text-sm text-ele-body">
          {sweepResult.map((o) => (
            <div key={o.mailbox}>
              {o.mailbox}: {o.isBacklogRun ? "first-run 12-month backlog sweep: " : ""}
              {o.threadsProcessed} thread{o.threadsProcessed === 1 ? "" : "s"} looked at in this run, of which{" "}
              {o.actionOwedCount} need a reply and {o.needsChaseCount} need chasing.{" "}
              {o.readFailedCount > 0 && `${o.readFailedCount} could not be read. `}
              {o.capped && "The per-run cap was reached; sweep again to keep catching up."}
            </div>
          ))}
          {/* THE PER-RUN NUMBERS ABOVE ARE NOT THE BACKLOG, and saying so here is the same correction
              the MCP tool's ...ThisRun rename makes for the assistant. A routine sweep only looks at
              about three days of mail, so reading its counts as the standing totals under-states the
              backlog by an order of magnitude -- which is exactly the inference that sent a session
              brief chasing a thousand threads that were never measured. */}
          {sweepStanding && (
            <div className="mt-2 border-t border-ele-hairline pt-2">
              Across the whole mailbox: {sweepStanding.actionOwed} needing a reply, {sweepStanding.needsChase} to chase,{" "}
              {sweepStanding.aged} aged, {sweepStanding.excluded} not triaged.
            </div>
          )}
        </div>
      )}

      {state === "loading" && <Loader />}
      {state === "error" && <p role="alert" className="text-sm text-ele-danger">Could not load the inbox triage list.</p>}

      {state === "ready" && (
        <>
          <section className="mb-6">
            <h2 className="mb-2 font-heading text-lg text-ele-strong">Action owed ({actionOwed.length})</h2>
            {actionOwed.length === 0 ? (
              <p className="text-sm text-ele-body">
                {aged.some((t) => t.state === "action_owed")
                  ? "Nothing new needs a reply. Some older threads are waiting in Aged, needs review below."
                  : "Nothing needs a reply right now."}
              </p>
            ) : (
              <table className="ele-list-table w-full text-left text-sm">
                <ThreadColgroup />
                <tbody>
                  {actionOwed.map((t) => (
                    <ThreadRow key={t.id} t={t} onDismiss={dismiss} dismissing={busyId === t.id} />
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="mb-6">
            <h2 className="mb-2 font-heading text-lg text-ele-strong">Needs chase ({needsChase.length})</h2>
            {needsChase.length === 0 ? (
              <p className="text-sm text-ele-body">
                {aged.some((t) => t.state === "needs_chase")
                  ? "Nothing new to chase. Some older threads are waiting in Aged, needs review below."
                  : "Nothing has gone quiet past the chase threshold."}
              </p>
            ) : (
              <table className="ele-list-table w-full text-left text-sm">
                <ThreadColgroup />
                <tbody>
                  {needsChase.map((t) => (
                    <ThreadRow key={t.id} t={t} onDismiss={dismiss} dismissing={busyId === t.id} />
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="mb-6">
            <button type="button" onClick={() => setShowAwaiting((v) => !v)} className="mb-2 font-heading text-lg text-ele-strong">
              {showAwaiting ? "▾" : "▸"} Awaiting reply ({awaiting.length})
            </button>
            {showAwaiting &&
              (awaiting.length === 0 ? (
                <p className="text-sm text-ele-body">Nothing is currently waiting on a reply.</p>
              ) : (
                <table className="ele-list-table w-full text-left text-sm">
                  <ThreadColgroup />
                  <tbody>
                    {awaiting.map((t) => (
                      <ThreadRow key={t.id} t={t} onDismiss={dismiss} dismissing={false} />
                    ))}
                  </tbody>
                </table>
              ))}
          </section>


          <section className="mb-6">
            <button type="button" onClick={() => setShowAged((v) => !v)} className="mb-2 font-heading text-lg text-ele-strong">
              {showAged ? "▾" : "▸"} Aged, needs review ({aged.length})
            </button>
            {showAged && (
              <>
                <p className="mb-2 text-sm text-ele-body">
                  Still owed, but waiting more than {agedThresholdDays} working days. These are kept out of the daily briefing so it stays
                  short. They are not dismissed and nothing has been deleted: work through them here when you have time.
                </p>
                {aged.length === 0 ? (
                  <p className="text-sm text-ele-body">Nothing has aged past the threshold.</p>
                ) : (
                  <table className="ele-list-table w-full text-left text-sm">
                    <ThreadColgroup />
                    <tbody>
                      {aged.map((t) => (
                        <ThreadRow key={t.id} t={t} onDismiss={dismiss} dismissing={busyId === t.id} />
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </section>

          <section className="mb-6">
            <button type="button" onClick={() => setShowExcluded((v) => !v)} className="mb-2 font-heading text-lg text-ele-strong">
              {showExcluded ? "▾" : "▸"} Not triaged ({excluded?.total ?? 0})
            </button>
            {showExcluded && (
              <>
                <p className="mb-2 text-sm text-ele-body">
                  Threads the platform never counts as needing a reply, because of who sent them. Shown here so nothing is suppressed
                  invisibly. If something below should have reached you, say so: the rule is a fixed list, not a guess.
                </p>
                {(excluded?.byReason.length ?? 0) > 0 && (
                  <p className="mb-2 text-sm text-ele-body">
                    {excluded!.byReason.map((r) => `${EXCLUSION_LABEL[r.reason] ?? r.reason}: ${r.count}`).join("  |  ")}
                  </p>
                )}
                {(excluded?.sample.length ?? 0) === 0 ? (
                  <p className="text-sm text-ele-body">Nothing is currently suppressed.</p>
                ) : (
                  <table className="ele-list-table w-full text-left text-sm">
                    <colgroup>
                      <col style={{ width: "50%" }} />
                      <col style={{ width: "28%" }} />
                      <col style={{ width: "22%" }} />
                    </colgroup>
                    <tbody>
                      {excluded!.sample.map((x) => (
                        <tr key={x.id} className="border-t border-ele-hairline">
                          <td className="py-2 text-sm text-ele-strong">{x.subject?.trim() || "(no subject)"}</td>
                          <td className="py-2 text-sm text-ele-body">{x.counterpartyEmail ?? "an unknown sender"}</td>
                          <td className="py-2 text-sm text-ele-body">{EXCLUSION_LABEL[x.reason] ?? x.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {excluded && excluded.total > excluded.sample.length && (
                  <p className="mt-2 text-sm text-ele-body">Showing the {excluded.sample.length} most recent of {excluded.total}.</p>
                )}
              </>
            )}
          </section>

          <section>
            <button type="button" onClick={() => setShowDismissed((v) => !v)} className="mb-2 font-heading text-lg text-ele-strong">
              {showDismissed ? "▾" : "▸"} Recently dismissed ({dismissed.length})
            </button>
            {showDismissed &&
              (dismissed.length === 0 ? (
                <p className="text-sm text-ele-body">Nothing dismissed recently.</p>
              ) : (
                <table className="ele-list-table w-full text-left text-sm">
                  <colgroup>
                    <col style={{ width: "65%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "13%" }} />
                  </colgroup>
                  <tbody>
                    {dismissed.map((d) => (
                      <tr key={d.id}>
                        <td className="cell-wrap">
                          <div className="font-semibold text-ele-strong">{d.subject?.trim() || "(no subject)"}</div>
                          <div className="text-xs text-ele-body">{d.counterpartyName ? `${d.counterpartyName} <${d.counterpartyEmail ?? "?"}>` : (d.counterpartyEmail ?? "Unknown sender")}</div>
                        </td>
                        <td className="whitespace-nowrap text-sm text-ele-body">Dismissed {formatDateTime(d.dismissedAt)}</td>
                        <td className="whitespace-nowrap text-right">
                          <Button variant="secondary" size="sm" onClick={() => restore(d.id)} disabled={busyId === d.id}>
                            {busyId === d.id ? "Restoring..." : "Restore"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}
          </section>
        </>
      )}
    </div>
  );
}
